import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from 'src/API2';
import API3 from 'src/API3';
import Logo from 'src/Assets/images/logo.png';
import { useNotificationContext } from 'src/NotificationContext';
import './dashboard.css';

// ── Inline icons (no external deps) ──────────────────────────────────
const Svg = ({ children, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const FaSignInAlt   = () => <Svg><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" /></Svg>;
const FaSignOutAlt  = () => <Svg><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></Svg>;
const FaUsers       = () => <Svg><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></Svg>;
const FaBell        = () => <Svg><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></Svg>;
const FaRegBell     = FaBell;
const FaMoneyBillWave = () => <Svg><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="3" /><path d="M6 12h.01M18 12h.01" /></Svg>;
const FaBed         = () => <Svg><path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8" /><path d="M2 17h20" /><path d="M6 10V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4" /></Svg>;
const FaCalendarAlt = () => <Svg><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></Svg>;
const FaEnvelope    = () => <Svg><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></Svg>;
const FaPhone       = () => <Svg><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" /></Svg>;
const FaHome        = () => <Svg><path d="M3 9.5 12 3l9 6.5V21a2 2 0 0 1-2 2h-4v-7h-6v7H5a2 2 0 0 1-2-2z" /></Svg>;
const FaCheckCircle = () => <Svg><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></Svg>;
const FaTimesCircle = () => <Svg><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></Svg>;
const FaClock       = () => <Svg><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></Svg>;
const FaUser        = () => <Svg><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></Svg>;
const FaChevronRight = () => <Svg size={14}><polyline points="9 18 15 12 9 6" /></Svg>;
const FaArrowRight  = () => <Svg><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></Svg>;

// ── Helpers ──────────────────────────────────────────────────────────
const toYmd = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const isSameDay = (a, b) => toYmd(a) === toYmd(b);

const fmtMoney = (n) => {
  const v = Math.round(Number(n) || 0);
  return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' DA';
};

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const fmtDateLong = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return ''; }
};

const nightsBetween = (a, b) => {
  const start = new Date(a); start.setHours(0, 0, 0, 0);
  const end = new Date(b); end.setHours(0, 0, 0, 0);
  const diff = (end - start) / 86400000;
  return Number.isFinite(diff) && diff > 0 ? Math.round(diff) : 0;
};

// ── Small primitives ─────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, tint, hint }) => (
  <div className={`dsh-stat dsh-stat--${tint || 'blue'}`}>
    <div className="dsh-stat__icon"><Icon /></div>
    <div className="dsh-stat__body">
      <span className="dsh-stat__label">{label}</span>
      <span className="dsh-stat__value">{value}</span>
      {hint ? <span className="dsh-stat__hint">{hint}</span> : null}
    </div>
  </div>
);

const EmptyRow = ({ icon: Icon, text }) => (
  <div className="dsh-empty-row">
    <Icon /><span>{text}</span>
  </div>
);

// ── Main Dashboard ───────────────────────────────────────────────────
const Dashboard = () => {
  const { notifications, setNotifications } = useNotificationContext();
  const [selected, setSelected] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loadingRes, setLoadingRes] = useState(true);
  const navigate = useNavigate();

  const staffName =
    localStorage.getItem('staffFullName') ||
    localStorage.getItem('fullname') ||
    'there';

  // Fetch reservations + room types (for occupancy)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [resResp, roomResp] = await Promise.all([
          API.get('reservations').catch(() => ({ data: { reservations: [] } })),
          API.get('rooms').catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setReservations(resResp?.data?.reservations || []);
        const roomsList = Array.isArray(roomResp?.data)
          ? roomResp.data
          : roomResp?.data?.rooms || [];
        setRooms(roomsList);
      } finally {
        if (!cancelled) setLoadingRes(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // ── Derived metrics ──
  const today = new Date();
  const todayYmd = toYmd(today);

  const {
    arrivalsToday,
    departuresToday,
    inHouse,
    revenueToday,
    occupancy30,
  } = useMemo(() => {
    const active = reservations.filter((r) =>
      r && r.status && String(r.status).toUpperCase() !== 'REJECTED'
    );

    const arrivalsToday = active.filter((r) => isSameDay(r.checkin_date, today));
    const departuresToday = active.filter((r) => isSameDay(r.checkout_date, today));
    const inHouse = active.filter((r) => {
      const ci = new Date(r.checkin_date); ci.setHours(0, 0, 0, 0);
      const co = new Date(r.checkout_date); co.setHours(0, 0, 0, 0);
      const d0 = new Date(today); d0.setHours(0, 0, 0, 0);
      return ci <= d0 && co > d0;
    });

    const revenueToday = arrivalsToday.reduce(
      (sum, r) => sum + Number(r.price || r.totalPrice || 0),
      0,
    );

    // 30-day occupancy heatmap: rooms-occupied per day / total-room-count
    const totalRooms = rooms.reduce(
      (sum, r) => sum + Number(r.count || r.total || 0),
      0,
    ) || rooms.length || 1;
    const occupancy30 = Array.from({ length: 30 }).map((_, i) => {
      const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + i);
      const occupied = active.filter((r) => {
        const ci = new Date(r.checkin_date); ci.setHours(0, 0, 0, 0);
        const co = new Date(r.checkout_date); co.setHours(0, 0, 0, 0);
        return ci <= d && co > d;
      }).length;
      return { date: d, occupied, rate: Math.min(1, occupied / totalRooms) };
    });

    return { arrivalsToday, departuresToday, inHouse, revenueToday, occupancy30 };
  }, [reservations, rooms, todayYmd]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendingCount = (notifications || []).filter((n) => !n.isRead).length;

  // ── Notifications handlers ──
  const handleNotifClick = async (notif) => {
    setSelected(notif);
    if (!notif.isRead) {
      try {
        await API.patch(`/notifications/${notif.id}/read`);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notif.id ? { ...n, isRead: true } : n)),
        );
      } catch (err) { console.error(err); }
    }
  };

  const handleStatusChange = async (id, notif, status) => {
    try {
      if (status === 'CANCELLED') {
        await API.patch(`/notifications/${id}/status`, { status });
        setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, status } : n)));
        await API3.put(`/hotelreservations/hrefuse/${notif?.hotel_id}`);
        window.location.reload();
      } else if (status === 'COMPLETED') {
        const notification = notifications.find((n) => n.id === id);
        navigate('/reservations/create', { state: { notification } });
      }
    } catch (err) {
      console.error(`Error updating notification status to ${status}:`, err);
    }
  };

  const statusDisabled =
    selected?.status === 'COMPLETED' || selected?.status === 'CANCELLED';

  return (
    <div className="dsh">
      {/* ─── Hero ─── */}
      <header className="dsh-hero">
        <div>
          <span className="dsh-hero__eyebrow">{today.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
          <h1 className="dsh-hero__title">Welcome back, {staffName.split(' ')[0]}</h1>
          <p className="dsh-hero__sub">
            Here's what's happening at the property today.
          </p>
        </div>
        <div className="dsh-hero__actions">
          <button type="button" className="dsh-btn dsh-btn--ghost" onClick={() => navigate('/reservations')}>
            <FaCalendarAlt /> View reservations
          </button>
          <button type="button" className="dsh-btn dsh-btn--primary" onClick={() => navigate('/reservations/create')}>
            <FaArrowRight /> New reservation
          </button>
        </div>
      </header>

      {/* ─── KPI Row ─── */}
      <section className="dsh-stats">
        <StatCard
          icon={FaSignInAlt}
          label="Arrivals today"
          value={loadingRes ? '—' : arrivalsToday.length}
          tint="blue"
          hint={arrivalsToday.length ? `${arrivalsToday.filter(r => r.status === 'PENDING').length} pending` : null}
        />
        <StatCard
          icon={FaSignOutAlt}
          label="Departures today"
          value={loadingRes ? '—' : departuresToday.length}
          tint="orange"
          hint={departuresToday.length ? 'Check-outs expected' : null}
        />
        <StatCard
          icon={FaUsers}
          label="In-house guests"
          value={loadingRes ? '—' : inHouse.length}
          tint="navy"
          hint={rooms.length ? `${rooms.length} room types` : null}
        />
        <StatCard
          icon={FaRegBell}
          label="Pending requests"
          value={pendingCount}
          tint="red"
          hint={pendingCount ? 'Needs attention' : 'All clear'}
        />
        <StatCard
          icon={FaMoneyBillWave}
          label="Revenue today"
          value={loadingRes ? '—' : fmtMoney(revenueToday)}
          tint="green"
          hint="From arrivals"
        />
      </section>

      <div className="dsh-grid">
        {/* ─── Today's timeline ─── */}
        <section className="dsh-card dsh-card--timeline">
          <header className="dsh-card__head">
            <div>
              <h3>Today's schedule</h3>
              <p>Arrivals and departures for {today.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</p>
            </div>
            <button className="dsh-link" onClick={() => navigate('/reservations')}>
              All reservations <FaChevronRight />
            </button>
          </header>

          <div className="dsh-timeline">
            <div className="dsh-timeline__col">
              <h4 className="dsh-timeline__title">
                <span className="dsh-dot dsh-dot--blue" /> Arrivals · {arrivalsToday.length}
              </h4>
              {arrivalsToday.length === 0 ? (
                <EmptyRow icon={FaSignInAlt} text="No arrivals today" />
              ) : (
                <ul className="dsh-timeline__list">
                  {arrivalsToday.slice(0, 6).map((r, i) => {
                    const u = (r.user && r.user[0]) || {};
                    return (
                      <li key={r.id || i} className="dsh-timeline__row">
                        <span className="dsh-avatar dsh-avatar--blue">
                          {(u.firstname || 'G')[0]}{(u.lastname || 'X')[0]}
                        </span>
                        <div className="dsh-timeline__info">
                          <span className="dsh-timeline__name">{u.firstname || '—'} {u.lastname || ''}</span>
                          <span className="dsh-timeline__meta">
                            <FaBed /> {r.room?.description?.slice(0, 24) || r.room?.code || 'Room'} · {nightsBetween(r.checkin_date, r.checkout_date)} night(s)
                          </span>
                        </div>
                        <span className={`dsh-badge dsh-badge--${String(r.status).toLowerCase()}`}>
                          {r.status}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="dsh-timeline__col">
              <h4 className="dsh-timeline__title">
                <span className="dsh-dot dsh-dot--orange" /> Departures · {departuresToday.length}
              </h4>
              {departuresToday.length === 0 ? (
                <EmptyRow icon={FaSignOutAlt} text="No departures today" />
              ) : (
                <ul className="dsh-timeline__list">
                  {departuresToday.slice(0, 6).map((r, i) => {
                    const u = (r.user && r.user[0]) || {};
                    return (
                      <li key={r.id || i} className="dsh-timeline__row">
                        <span className="dsh-avatar dsh-avatar--orange">
                          {(u.firstname || 'G')[0]}{(u.lastname || 'X')[0]}
                        </span>
                        <div className="dsh-timeline__info">
                          <span className="dsh-timeline__name">{u.firstname || '—'} {u.lastname || ''}</span>
                          <span className="dsh-timeline__meta">
                            <FaBed /> {r.room?.description?.slice(0, 24) || r.room?.code || 'Room'}
                          </span>
                        </div>
                        <span className={`dsh-badge dsh-badge--${String(r.status).toLowerCase()}`}>
                          {r.status}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </section>

        {/* ─── Occupancy heatmap (30 days) ─── */}
        <section className="dsh-card dsh-card--heatmap">
          <header className="dsh-card__head">
            <div>
              <h3>Occupancy outlook</h3>
              <p>Next 30 days · light = free, dark = full</p>
            </div>
            <button className="dsh-link" onClick={() => navigate('/calendar')}>
              Open calendar <FaChevronRight />
            </button>
          </header>
          <div className="dsh-heatmap">
            {occupancy30.map((d, i) => (
              <div
                key={i}
                className="dsh-heat"
                title={`${d.date.toLocaleDateString('fr-FR')} · ${d.occupied} reservation(s)`}
                style={{ background: `rgba(35, 158, 254, ${0.12 + d.rate * 0.75})` }}
              >
                <span className="dsh-heat__day">{d.date.getDate()}</span>
              </div>
            ))}
          </div>
          <div className="dsh-heatmap__legend">
            <span>Free</span>
            <div className="dsh-heatmap__scale" />
            <span>Full</span>
          </div>
        </section>

        {/* ─── Notifications column ─── */}
        <aside className="dsh-card dsh-card--notifs">
          <header className="dsh-card__head">
            <div>
              <h3>Notifications</h3>
              <p>{pendingCount} unread · last 5 days</p>
            </div>
          </header>
          {!notifications || notifications.length === 0 ? (
            <EmptyRow icon={FaBell} text="Nothing pending. You're all set." />
          ) : (
            <ul className="dsh-notif-list">
              {notifications.slice(0, 8).map((n) => (
                <li
                  key={n.id}
                  className={`dsh-notif ${n.isRead ? '' : 'is-unread'} ${selected?.id === n.id ? 'is-active' : ''}`}
                  onClick={() => handleNotifClick(n)}
                >
                  <div className="dsh-notif__dot" />
                  <div className="dsh-notif__body">
                    <div className="dsh-notif__row">
                      <span className="dsh-notif__title">{n.title}</span>
                      <span className="dsh-notif__time">{fmtTime(n.createdAt)}</span>
                    </div>
                    <span className="dsh-notif__msg">{n.message}</span>
                    <span className={`dsh-badge dsh-badge--${String(n.status).toLowerCase()}`}>{n.status}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>

      {/* ─── Detail drawer (opens when a notification is clicked) ─── */}
      {selected && (
        <div className="dsh-drawer-scrim" onClick={() => setSelected(null)}>
          <aside
            className="dsh-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Reservation request details"
          >
            <header className="dsh-drawer__head">
              <div>
                <span className="dsh-drawer__eyebrow">Reservation request</span>
                <h2>
                  {(() => {
                    const firstApplicant = Array.isArray(selected.applicants) ? selected.applicants[0] : null;
                    if (firstApplicant && (firstApplicant.firstname || firstApplicant.lastname)) {
                      return `${firstApplicant.firstname || ''} ${firstApplicant.lastname || ''}`.trim();
                    }
                    return `${selected.name || ''} ${selected.surnom || ''}`.trim();
                  })()}
                </h2>
              </div>
              <button className="dsh-drawer__close" onClick={() => setSelected(null)} aria-label="Close">
                <FaTimesCircle />
              </button>
            </header>

            <div className="dsh-drawer__body">
              <section className="dsh-drawer__section">
                <h4>Guest</h4>
                <div className="dsh-drawer__grid">
                  <div><FaEnvelope /><span>{selected.email}</span></div>
                  <div><FaPhone /><span>{selected.phone}</span></div>
                  <div><FaHome /><span>{selected.address || '—'}, {selected.zipcode || '—'}</span></div>
                </div>
              </section>

              <section className="dsh-drawer__section">
                <h4>Stay</h4>
                <div className="dsh-drawer__grid">
                  <div><FaSignInAlt /><span>{fmtDateLong(selected.checkinDate)}</span></div>
                  <div><FaSignOutAlt /><span>{fmtDateLong(selected.checkoutDate)}</span></div>
                  <div><FaClock /><span>{selected.totalNights} night(s)</span></div>
                  <div>
                    <FaMoneyBillWave />
                    <span>{fmtMoney(selected.totalPrice || 0)}</span>
                  </div>
                </div>
              </section>

              {Array.isArray(selected.applicants) && selected.applicants.length > 0 && (
                <section className="dsh-drawer__section">
                  <h4>Applicants ({selected.applicants.length})</h4>
                  <ul className="dsh-applicants">
                    {selected.applicants.map((a, i) => (
                      <li key={i}>
                        <span className="dsh-applicants__avatar"><FaUser /></span>
                        <div>
                          <strong>{a.firstname} {a.lastname}</strong>
                          <span>{a.documentType} · {a.documentNumber} · {a.gender}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>

            {!statusDisabled && (
              <footer className="dsh-drawer__foot">
                <button
                  className="dsh-btn dsh-btn--ghost"
                  onClick={() => handleStatusChange(selected.id, selected, 'CANCELLED')}
                >
                  <FaTimesCircle /> Ignore
                </button>
                <button
                  className="dsh-btn dsh-btn--primary"
                  onClick={() => handleStatusChange(selected.id, selected, 'COMPLETED')}
                >
                  <FaCheckCircle /> Complete reservation
                </button>
              </footer>
            )}
          </aside>
        </div>
      )}

      {/* Hidden logo for old CSS class — kept for safety, not rendered visibly */}
      <img src={Logo} alt="" style={{ display: 'none' }} />
    </div>
  );
};

export default Dashboard;
